/**
 Original: https://github.com/kalmecak/moment-business-days/blob/master/index.js
 The MIT License (MIT)

Copyright (c) 2015 christian

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
 **/

let moment = require('moment');
const holidaysFactory = require('../holidaysFactory');

const HOLIDAY_FORMAT = 'YYYY-MM-DD';
const workingWeekdays = [1, 2, 3, 4, 5];
const holidaysCache = {};

moment.getHolidays = function (year) {
  let holidays = holidaysCache[year];
  if (!holidays) {
    holidays = holidaysFactory(year);
    holidaysCache[year] = holidays;
  }
  return holidays;
};
moment.fn.isHoliday = function () {
  const year = this.year();
  let holidays = moment.getHolidays(year);
  return holidays.includes(this.format(HOLIDAY_FORMAT));
};

moment.fn.isBusinessDay = function () {
  if (this.isHoliday()) return false;
  if (workingWeekdays.indexOf(this.day()) >= 0) return true;

  return false;
};

moment.fn.businessDaysIntoMonth = function () {
  if (!this.isValid()) {
    return NaN;
  }
  const businessDay = this.isBusinessDay() ? this : this.prevBusinessDay();
  const monthBusinessDays = businessDay.monthBusinessDays();
  let businessDaysIntoMonth;
  monthBusinessDays.map(function (day, index) {
    if (day.format('M/DD/YY') === businessDay.format('M/DD/YY')) {
      businessDaysIntoMonth = index + 1;
    }
  });
  return businessDaysIntoMonth;
};

moment.fn.businessDiff = function (param, relative) {
  const d1 = this.clone();
  const d2 = param.clone();
  const positive = d1 >= d2;
  const start = d1 < d2 ? d1 : d2;
  const end = d2 > d1 ? d2 : d1;

  let daysBetween = 0;

  if (start.format('DD/MM/YYYY') === end.format('DD/MM/YYYY')) {
    return daysBetween;
  }

  while (start < end) {
    if (start.isBusinessDay()) {
      daysBetween++;
    }
    start.add(1, 'd');
  }

  if (relative) {
    return positive ? daysBetween : -daysBetween;
  }

  return daysBetween;
};

moment.fn.businessAdd = function (number, period) {
  const day = this.clone();
  if (!day.isValid()) {
    return day;
  }

  if (number < 0) {
    number = Math.round(-1 * number) * -1;
  } else {
    number = Math.round(number);
  }

  const signal = number < 0 ? -1 : 1;
  period = typeof period !== 'undefined' ? period : 'days';

  let remaining = Math.abs(number);
  while (remaining > 0) {
    day.add(signal, period);

    if (day.isBusinessDay()) {
      remaining--;
    }
  }

  return day;
};

moment.fn.businessSubtract = function (number, period) {
  return this.businessAdd(-number, period);
};

moment.fn.nextBusinessDay = function () {
  const searchLimit = 7;
  let currentDay = this.clone();

  let loopIndex = 1;
  while (loopIndex < searchLimit) {
    currentDay = currentDay.add(1, 'day');

    if (currentDay.isBusinessDay()) break;
    loopIndex += 1;
  }

  return currentDay;
};

moment.fn.prevBusinessDay = function () {
  const searchLimit = 7;
  let currentDay = this.clone();

  let loopIndex = 1;
  while (loopIndex < searchLimit) {
    currentDay = currentDay.subtract(1, 'day');

    if (currentDay.isBusinessDay()) break;
    loopIndex += 1;
  }

  return currentDay;
};

moment.fn.monthBusinessDays = function (partialEndDate) {
  if (!this.isValid()) {
    return [];
  }
  const me = this.clone();
  const day = me.clone().startOf('month');
  const end = partialEndDate ? partialEndDate : me.clone().endOf('month');
  const daysArr = [];
  let done = false;
  while (!done) {
    if (day.isBusinessDay()) {
      daysArr.push(day.clone());
    }
    if (end.diff(day.add(1, 'd')) < 0) {
      done = true;
    }
  }
  return daysArr;
};

moment.fn.monthNaturalDays = function (fromToday) {
  if (!this.isValid()) {
    return [];
  }
  const me = this.clone();
  const day = fromToday ? me.clone() : me.clone().startOf('month');
  const end = me.clone().endOf('month');
  const daysArr = [];
  let done = false;
  while (!done) {
    daysArr.push(day.clone());
    if (end.diff(day.add(1, 'd')) < 0) {
      done = true;
    }
  }
  return daysArr;
};

moment.fn.monthBusinessWeeks = function (fromToday) {
  fromToday = fromToday || false;
  const me = this.clone();
  const startDate = fromToday ? me.clone() : me.clone().startOf('month');
  return getBusinessWeeks(this, fromToday, null, startDate);
};

moment.fn.businessWeeksBetween = function (endDate) {
  const me = this.clone();
  const startDate = me.clone();
  return getBusinessWeeks(this, false, endDate, startDate);
};

const getBusinessWeeks = function (self, fromToday, endDate, startDate) {
  if (!self.isValid()) {
    return [];
  }
  const me = self.clone();
  const day = startDate;
  const end = endDate ? moment(endDate).clone() : me.clone().endOf('month');
  const weeksArr = [];
  let daysArr = [];
  let done = false;

  while (!done) {
    if (day.day() >= 1 && day.day() < 6) {
      daysArr.push(day.clone());
    }
    if (day.day() === 5) {
      weeksArr.push(daysArr);
      daysArr = [];
    }
    if (end.diff(day.add(1, 'd')) < 0) {
      if (daysArr.length < 5) {
        weeksArr.push(daysArr);
      }
      done = true;
    }
  }
  return weeksArr;
};

moment.fn.monthNaturalWeeks = function (fromToday) {
  if (!this.isValid()) {
    return [];
  }
  const me = this.clone();
  const day = fromToday ? me.clone() : me.clone().startOf('month');
  const end = me.clone().endOf('month');
  const weeksArr = [];
  let daysArr = [];
  let done = false;

  while (!done) {
    daysArr.push(day.clone());
    if (day.day() === 6) {
      weeksArr.push(daysArr);
      daysArr = [];
    }
    if (end.diff(day.add(1, 'd')) < 0) {
      if (daysArr.length < 7) {
        weeksArr.push(daysArr);
      }
      done = true;
    }
  }
  return weeksArr;
};

moment.fn.businessDaysAdd = function (number) {
  const numericDirection = number < 0 ? -1 : 1;
  let currentDay = this.clone();
  let daysRemaining = Math.abs(number);

  while (daysRemaining > 0) {
    currentDay = currentDay.add(numericDirection, 'd');

    if (currentDay.isBusinessDay()) daysRemaining -= 1;
  }

  return currentDay;
};

moment.fn.businessDaysSubtract = function (number) {
  let currentDay = this.clone();

  currentDay = currentDay.businessDaysAdd(number * -1);

  return currentDay;
};

if (typeof module != 'undefined' && module.exports) {
  module.exports = moment;
}
